import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Mail, Phone, Calendar, Trash2, Edit, Save, X
} from 'lucide-react';

const getStatusClassName = (status) => {
  if (status === 'Completed') return 'bg-green-100 text-green-700';
  if (status === 'In Progress') return 'bg-yellow-100 text-yellow-700';
  if (status === 'Contacted') return 'bg-blue-100 text-blue-700';
  return 'bg-gray-100 text-gray-600';
};

const getPriorityClassName = (priority) => {
  if (priority === 'High') return 'bg-red-100 text-red-700';
  if (priority === 'Low') return 'bg-gray-100 text-gray-600';
  return 'bg-blue-100 text-blue-700';
};

const SubmissionRow = ({ submission, onEdit, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    priority: submission.priority || 'Normal',
    followUpDate: submission.followUpDate || '',
    notes: submission.notes || '',
    status: submission.status || 'New'
  });

  const handleSave = async () => {
    const success = await onEdit(submission.id, editForm);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this submission?')) {
      onDelete(submission.id);
    }
  };

  if (isEditing) {
    return (
      <div 
        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
        data-testid={`submission-${submission.id}`}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Select 
              value={editForm.status} 
              onValueChange={(v) => setEditForm(prev => ({...prev, status: v}))}
            >
              <SelectTrigger data-testid={`edit-status-${submission.id}`}>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="Contacted">Contacted</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select 
              value={editForm.priority} 
              onValueChange={(v) => setEditForm(prev => ({...prev, priority: v}))}
            >
              <SelectTrigger data-testid={`edit-priority-${submission.id}`}>
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Normal">Normal</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={editForm.followUpDate || ''}
              onChange={(e) => setEditForm(prev => ({...prev, followUpDate: e.target.value}))}
              placeholder="Follow-up date"
              data-testid={`edit-follow-up-${submission.id}`}
            />
          </div>
          <Textarea
            value={editForm.notes}
            onChange={(e) => setEditForm(prev => ({...prev, notes: e.target.value}))}
            placeholder="Internal notes..."
            rows={2}
            data-testid={`edit-notes-${submission.id}`}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} className="bg-[#1877F2]" data-testid={`save-${submission.id}`}>
              <Save className="w-4 h-4 mr-1" /> Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => setIsEditing(false)} data-testid={`cancel-${submission.id}`}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
      data-testid={`submission-${submission.id}`}
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              submission.type === 'join_study' 
                ? 'bg-green-100 text-green-700' 
                : 'bg-purple-100 text-purple-700'
            }`}>
              {submission.type === 'join_study' ? 'Study' : 'Contact'}
            </span>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityClassName(submission.priority)}`}>
              {submission.priority || 'Normal'}
            </span>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusClassName(submission.status)}`}>
              {submission.status || 'New'}
            </span>
          </div>
          <h3 className="font-semibold text-gray-900">{submission.name}</h3>
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mt-1">
            <span className="flex items-center">
              <Mail className="w-3 h-3 mr-1" /> {submission.email}
            </span>
            {submission.phone && (
              <span className="flex items-center">
                <Phone className="w-3 h-3 mr-1" /> {submission.phone}
              </span>
            )}
            <span className="flex items-center">
              <Calendar className="w-3 h-3 mr-1" /> 
              {new Date(submission.timestamp).toLocaleDateString()}
            </span>
          </div>
          {submission.type === 'join_study' && (
            <p className="text-sm text-[#1877F2] font-medium mt-1">
              Condition: {submission.condition} | Age: {submission.age}
            </p>
          )}
          {submission.type === 'contact' && submission.subject && (
            <p className="text-sm text-gray-700 mt-1">
              Subject: {submission.subject}
            </p>
          )}
          {submission.message && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{submission.message}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => setIsEditing(true)}
            data-testid={`edit-${submission.id}`}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            className="text-red-600 hover:bg-red-50"
            onClick={handleDelete}
            data-testid={`delete-${submission.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubmissionRow;
